import React, { useState } from "react";

import { useCreativeElements } from "../../../../services/useCreativeElements";
import SessionChoiceLine, { typeLabel } from "../../../../components/shared/SessionChoiceLine";
import VideoDirectionPanel from "./VideoDirectionPanel";

/**
 * Workstation · Ads & Creative — **step 6, Creative Elements, in v2.**
 *
 * WAVE 3, SCREEN 6. Behaviour is `services/useCreativeElements`, shared with v1.
 *
 * The step where a script is submitted to production. The defect the sweep
 * found was live: it scoped strictly, and only 3 of 288 scripts carry a session
 * — so it showed the operator nothing at all.
 *
 * TWO FOUNDER RULINGS, 2026-09-09, and they change what this step is:
 *
 *   1. **It no longer asks for the creative type.** "Creative Elements (step 6)
 *      still shows its own format dropdown (AI Video Trello, VE, UGC, etc.) —
 *      that shouldn't be there." The choice is made once on step 1 and this step
 *      shows it. The dropdown is GONE rather than disabled: a control that
 *      cannot be used is still a control, and it was the fourth place the same
 *      question was asked.
 *
 *      That dropdown was also the reason he said "nothing has changed". 90 of
 *      288 scripts carry `creative_type: "AI"` with no destination, which
 *      matches none of the four option VALUES — and a `<select>` handed an
 *      unmatched value silently shows its placeholder. A chosen type rendered as
 *      "Choose type…".
 *
 *   2. **It shows the visual direction and the video prompts.** "Creative
 *      Elements still has no video direction/prompt content at all." See
 *      `VideoDirectionPanel` — the direction was on every script all along, and
 *      the prompts were built inside the submit and stored where nothing read
 *      them.
 */
export default function ElementsStep({ offer, sessionId, isNewSession,
                                       sessionStartTime, angleId, refreshKey,
                                       sessionMeta, onGoToStep, onComplete }) {
  const E = useCreativeElements({ offer, sessionId, isNewSession, sessionStartTime,
                                  angleId, onComplete, sessionMeta, refreshKey });

  const [open, setOpen] = useState(false);
  const [bodyId, setBodyId] = useState("");
  const [ctaId, setCtaId] = useState("");
  const [hookIds, setHookIds] = useState([]);
  const [batch, setBatch] = useState(null);   // { routed, failures }
  const [showDirection, setShowDirection] = useState(null);  // script_id

  // The assembly panel asked for a channel and a creative type of its own —
  // the fourth and fifth times the same question was asked in one wizard. Both
  // come from the session now: a creative recombined from this session's parts
  // is built the way this session is built.
  const sessionType = E.sessionChoice
    ? String(E.sessionChoice).split(":")[0]
    : null;
  const sessionChannel = String(sessionMeta?.video_format || "")
    .toUpperCase().startsWith("TIKTOK") ? "TIKTOK" : "META";

  const toggleHook = (id) =>
    setHookIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="ws-step" data-testid="ws-elements-step">
      <div className="ws-step-head">
        <div>
          <h3 className="ws-step-title">Creative Elements</h3>
          <p className="dim">
            Check the visual direction, then submit each script to production.
            The creative type comes from step 1 — this step does not ask. Loose
            hooks, bodies and CTAs can also be recombined into new creatives.
          </p>
        </div>
        {E.unrouted.length > 0 ? (
          <div className="ws-elements-head-actions">
            {/* ONE PRESS, EVERY SCRIPT. The Founder, 2026-09-10: "I wanted to
                ensure that both prompts existed without having to actually
                click on both." */}
            <button type="button" className="btn-ghost btn-sm"
                    disabled={E.checking}
                    onClick={E.checkAllPrompts}
                    data-testid="ws-elements-check-prompts">
              {E.checking ? "Building the prompts…" : "Check the prompts for all"}
            </button>
            <button type="button" className="btn-primary btn-sm"
                    disabled={E.busy === "__all__"}
                    onClick={async () => setBatch(await E.submitAll(E.unrouted))}
                    data-testid="ws-elements-submit-all">
              {E.busy === "__all__" ? "Submitting…" : `Submit all ${E.unrouted.length}`}
            </button>
          </div>
        ) : null}
      </div>

      {/* WHAT SUBMIT ACTUALLY DOES, and how long it takes.
          The Founder, 2026-09-10: "I hit submit on step 6 and it says
          submitting and hangs there." It was not hung. Each AI submit makes
          four to five model calls, builds the brief PDF, uploads it to Drive and
          opens a Trello card — measured on his own run at roughly a minute per
          script. A button that says only "Submitting…" for that long is
          indistinguishable from one that has died. */}
      {E.busy === "__all__" || E.busy ? (
        <p className="ws-step-note" role="status" data-testid="ws-elements-submit-progress">
          Submitting builds the brief, writes the prompts, uploads to Drive and
          opens a Trello card for whoever makes the video. It takes about a
          minute per script — this is still working.
        </p>
      ) : null}

      {/* What step 1 decided, read-only, and the way back if it was never
          decided. This is what replaced the dropdown. */}
      <SessionChoiceLine sessionMeta={sessionMeta} onGoToStep={onGoToStep}
                         testidPrefix="ws-elements-choice" />

      {E.loadState === "loading" ? (
        <div className="state ws-state" data-testid="ws-elements-loading">Reading this session's scripts…</div>
      ) : null}

      {E.loadState === "failed" ? (
        <div className="state ws-state state-error" role="alert" data-testid="ws-elements-load-failed">
          <p>The scripts could not be read: {E.loadError}</p>
          <button type="button" className="btn-ghost btn-sm" onClick={E.retryLoad}
                  data-testid="ws-elements-retry">Try again</button>
        </div>
      ) : null}

      {E.loadState === "ok" && E.scripts.length === 0 ? (
        <div className="state ws-state" data-testid="ws-elements-empty">
          No scripts in this session yet.
        </div>
      ) : null}

      {E.note ? (
        <p className="ws-step-note" role="status" data-testid="ws-elements-note">{E.note}</p>
      ) : null}
      {E.actionError ? (
        <p className="ws-step-note state-error" role="alert" data-testid="ws-elements-error">
          {E.actionError}
        </p>
      ) : null}

      {/* A PARTIAL BATCH IS ITS OWN OUTCOME. Three routed and one refused is
          neither done nor failed, and the one that did not go is named. */}
      {batch?.failures?.length ? (
        <div className="state ws-state state-error" role="alert" data-testid="ws-elements-batch-failures">
          <p>{batch.routed} submitted, {batch.failures.length} refused:</p>
          <ul>
            {batch.failures.map((f) => (
              <li key={f.label}><strong>{f.label}</strong> — {f.detail}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {E.scripts.length > 0 ? (
        <ul className="ws-elements-list">
          {E.scripts.map((s) => {
            const id = s.script_id;
            const routed = !!s.production_id;
            return (
              <li key={id} className="ws-elements-row" data-testid={`ws-elements-script-${id}`}>
                <span className="ws-script-n">{s.body_element_number || id}</span>
                <span className="dim">{s.platform}</span>
                {s.cta_element_number ? (
                  <span className="ws-chip">{s.cta_element_number}</span>
                ) : (
                  <span className="ws-chip is-warn" data-testid={`ws-elements-nocta-${id}`}>no CTA</span>
                )}
                {/* SHOWN, NOT ASKED. The script's own type when it has one,
                    otherwise the session's — and named in words, so a bare
                    "AI" reads as a real answer instead of a placeholder. */}
                <span className="ws-elements-type"
                      data-testid={`ws-elements-type-${id}`}>
                  {typeLabel(E.effectiveType(s))
                    || "no creative type — set it on step 1"}
                </span>
                {/* CHECKED, NOT CHECKED, and FAILED are three states. A row
                    with no chip has not been checked — it is not a pass. */}
                {E.promptChecks[id] ? (
                  <span className={"ws-elements-promptstate "
                        + (E.promptChecks[id].state === "ok" ? "ws-chip" : "ws-chip is-warn")}
                        data-testid={`ws-elements-promptstate-${id}`}>
                    {E.promptChecks[id].state === "check_failed"
                      ? `prompt check failed: ${E.promptChecks[id].error}`
                      : E.promptChecks[id].state === "error"
                        ? "no prompts could be built"
                        : E.promptChecks[id].short || E.promptChecks[id].empty
                          ? `${E.promptChecks[id].clips} clips, `
                            + `${E.promptChecks[id].short + E.promptChecks[id].empty} not usable`
                          : `${E.promptChecks[id].clips} clip prompts ready`}
                  </span>
                ) : null}
                <button type="button" className="btn-ghost btn-sm"
                        onClick={() => setShowDirection((v) => (v === id ? null : id))}
                        aria-expanded={showDirection === id}
                        data-testid={`ws-elements-direction-toggle-${id}`}>
                  {showDirection === id ? "Hide direction" : "Visual direction & prompts"}
                </button>
                {!s.creative_type && E.sessionChoice ? (
                  <span className="dim ws-elements-inherited"
                        data-testid={`ws-elements-inherited-${id}`}>
                    from this session
                  </span>
                ) : null}
                {routed ? (
                  <span className="ws-chip" data-testid={`ws-elements-routed-${id}`}>submitted</span>
                ) : (
                  <button type="button" className="btn-ghost btn-sm"
                          disabled={E.busy === id || !E.effectiveType(s)}
                          onClick={() => E.routeToProduction(id, E.effectiveType(s))}
                          data-testid={`ws-elements-route-${id}`}>
                    {E.busy === id ? "Submitting…" : "Submit"}
                  </button>
                )}
                {showDirection === id ? (
                  <VideoDirectionPanel script={s} />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {E.hiddenOtherSession || E.hiddenUnstamped ? (
        <p className="dim ws-step-note" data-testid="ws-elements-hidden-note">
          Not shown: {E.hiddenOtherSession} from other sessions;{" "}
          {E.hiddenUnstamped} that predate session tracking.
        </p>
      ) : null}
      {E.predateNote > 0 ? (
        <p className="dim ws-step-note" data-testid="ws-elements-predate-note">
          Showing {E.predateNote} script{E.predateNote > 1 ? "s" : ""} that
          predate session tracking — Cipher cannot tell which session made them.
        </p>
      ) : null}

      {/* ── Modular assembly ──────────────────────────────────────────────── */}
      <div className="ws-assembly">
        <button type="button" className="btn-ghost btn-sm"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid="ws-assembly-toggle">
          {open ? "Close assembly" : "Recombine elements"}
        </button>

        {open ? (
          E.libraryState === "failed" ? (
            <div className="state ws-state state-error" role="alert"
                 data-testid="ws-assembly-library-failed">
              <p>The element library could not be read: {E.libraryError}</p>
              <p className="dim">
                Empty pickers would look like an offer with no elements. It is
                not — the read failed.
              </p>
              <button type="button" className="btn-ghost btn-sm" onClick={E.retryLibrary}
                      data-testid="ws-assembly-retry">Try again</button>
            </div>
          ) : (
            <div className="ws-assembly-body" data-testid="ws-assembly">
              <p className="dim">
                One body and one CTA, tested against every hook you tick — that
                is one creative per hook.
              </p>
              <div className="ws-assembly-row">
                <label>
                  <span className="dim">Body</span>
                  <select value={bodyId} onChange={(e) => setBodyId(e.target.value)}
                          data-testid="ws-assembly-body">
                    <option value="">Choose…</option>
                    {E.library.BODY.map((b) => (
                      <option key={b.element_id} value={b.element_id}>
                        {b.element_number || "—"} · {String(b.content || "").slice(0, 60)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="dim">CTA</span>
                  <select value={ctaId} onChange={(e) => setCtaId(e.target.value)}
                          data-testid="ws-assembly-cta">
                    <option value="">Choose…</option>
                    {E.library.CTA.map((c) => (
                      <option key={c.element_id} value={c.element_id}>
                        {c.element_number || "—"} · {String(c.content || "").slice(0, 60)}
                      </option>
                    ))}
                  </select>
                </label>
                {/* NOT ASKED HERE EITHER. These were the fourth and fifth
                    dropdowns for a question step 1 answers. */}
                <p className="dim" data-testid="ws-assembly-inherited">
                  Built as {typeLabel(E.sessionChoice) || "no creative type yet"}{" "}
                  for {sessionChannel} — from step 1.
                </p>
              </div>

              <p className="dim">Hooks to test ({hookIds.length} selected)</p>
              <ul className="ws-assembly-hooks">
                {E.library.HOOK.map((h) => (
                  <li key={h.element_id}>
                    <label>
                      <input type="checkbox" checked={hookIds.includes(h.element_id)}
                             onChange={() => toggleHook(h.element_id)}
                             data-testid={`ws-assembly-hook-${h.element_id}`} />
                      <span>{h.element_number || "—"} · {String(h.content || "").slice(0, 70)}</span>
                    </label>
                  </li>
                ))}
              </ul>

              <button type="button" className="btn-primary btn-sm"
                      disabled={E.busy === "__assembly__" || !sessionType}
                      onClick={() => E.assemble({ bodyId, ctaId, hookIds,
                                                  channel: sessionChannel,
                                                  creativeType: sessionType })}
                      data-testid="ws-assembly-run">
                {E.busy === "__assembly__" ? "Assembling…" : `Assemble ${hookIds.length || ""}`}
              </button>
            </div>
          )
        ) : null}
      </div>

      {E.scripts.length > 0 ? (
        <div className="ws-step-foot">
          <div className="ws-cta-confirm">
            <span className="dim">
              {E.scripts.length - E.unrouted.length} of {E.scripts.length} submitted
            </span>
            <button type="button" className="btn-primary btn-sm"
                    onClick={() => E.proceed && E.proceed()}
                    data-testid="ws-elements-continue">Continue</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
